import { supabase } from './supabase';
import { syncManager } from './syncManager';
import { merge3, sameJson } from './syncDoc';
import { accountGeneration } from './accountStorage';
import type { RefreshOutcome } from './syncBlob';

// ─── Whole-row sync for stores whose rows are keyed by their own id ─────────
// ⚠️⚠️ A ROW DELETED ON ANOTHER DEVICE OR TAB MUST STAY DELETED. The habit fix (os#123,
// live 0.7.28.1.0) as one helper, for tasks, events, journal entries, routines, routine
// folders and task folders. Their writes were `upsert(…, onConflict: 'id')`, so a tab whose
// cache still held a row deleted elsewhere re-INSERTED it on its next write (201 Created);
// and their loads kept every cached row the cloud no longer had, so the ghosts survived
// reloads and rode along on every write that touched many rows (a drag-reorder, a folder
// replay, the load-time subtask migration). Now:
// - The store keeps `unsyncedIds`: rows created HERE whose insert the cloud hasn't confirmed.
//   Only those are ever INSERTED; a 23505 ("already there") counts as confirmed. Every other
//   write is `update(…).eq('id')` (+ the compare-and-swap below) `.select('id, updated_at')`.
// - An UPDATE that finds no row means the row was deleted elsewhere: it is dropped here and
//   never re-created. A FAILED request is not a deletion: the row stays.
// - A load keeps a cached row the cloud didn't return only while it is unsynced (`mergeLoad`).
// - A load SAVES what it kept over the cloud — an unsynced row, an unsaved edit (`mergeLoad`'s
//   `kept` → `writeBack`, 2026-10-20). It used to keep them without writing them: a row made, or
//   an edit, whose write never landed (offline, or a sign-out on its way) stayed on this device
//   until its next edit.
// - ⚠️⚠️ NO CLOCK DECIDES WHICH COPY WINS (2026-10-20). The load kept a cached copy whose
//   `updatedAt` was later than the cloud's — this device's clock against another device's — and,
//   since it saves what it keeps, a device whose clock ran fast WROTE its old copy over another
//   device's later edit; one whose clock ran slow lost a real unsaved edit; task folders (the
//   server stamps theirs) re-wrote every saved folder on every load. Now a load decides from
//   two facts the device records (`RowMarks`, persisted with the store):
//   · `dirtyIds`: rows edited here (every `save`) whose latest save the cloud hasn't confirmed;
//   · `cloudVersions`: each row's `updated_at` as this device last saw it in the cloud (its
//     load, or its own confirmed write).
//   Only an unsaved edit of the row as the cloud still has it wins — and is written. A copy
//   nobody edited here takes the cloud's. `updated_at` is only ever compared for EQUALITY, so
//   whose clock stamped it doesn't matter (trigger on tasks and task_folders; the writer's own on
//   the other four).
// - ⚠️⚠️ AN UNSAVED EDIT OF A ROW ANOTHER DEVICE HAS WRITTEN SINCE IS MERGED, NOT DROPPED (owner,
//   2026-10-20). Its `updated_at` moved: this copy is whole, so writing it would undo that device's
//   edit — os#141 dropped it (a phone offline, a sign-out before the write landed: the edit lost).
//   Now each unsaved row keeps its BASE, persisted with the marks (`RowMarks.dirtyBases`): the row
//   as the cloud held it at its recorded version. The load puts this device's edits since that base
//   on top of the cloud's copy (`mergeRow`, the save's rule below: a field both changed takes this
//   device's) and saves the result. A row with no saved base (a cache from before 2026-10-20) still
//   takes the cloud's copy, the edit dropped (warned).
// - ⚠️⚠️ A SAVE NEVER WRITES A STALE COPY OVER ANOTHER DEVICE'S EDIT (2026-10-20). A tab kept the
//   rows it loaded until it reloaded (the focus refresh below only narrows that window), and a
//   write sent the WHOLE row unconditionally: a tab or device open since before another device's
//   edit wrote its old copy over that edit on its next edit of the row — whatever field it
//   changed. Now:
//   · the UPDATE is a compare-and-swap on the row's `updated_at` as this device last saw it
//     (`cloudVersions`; matched to the millisecond — a trigger stamps microseconds). Every write
//     moves it (the trigger on tasks and task_folders; each writer's own stamp on the other four);
//   · 0 rows → the row is read again: gone → deleted elsewhere (the os#123 rule, as before); there →
//     another device wrote since. Its copy wins every field this device didn't change since it last
//     saw the row (its BASE), this device's edits go on top — the field rule of syncDoc's three-way
//     merge (`merge3`): a field both changed takes this device's, the later edit. The merged row is
//     written the same way (5 tries), and shown here, with any edit made meanwhile kept on top;
//   · the base is held in memory: each row as the load read it (`seen`), as this device's write
//     left it, as the saved copy hands it back (`restore`) — for an unsaved row, its saved base, so
//     an unsaved edit from before a reload isn't taken for the cloud's. A row with no base at all:
//     the cloud's copy wins, the edit is dropped (warned);
//   · a version never recorded (a row not yet read since os#141) → a plain UPDATE, as before; its
//     answer records the version.
// - ⭐ A TAB THAT COMES BACK SHOWS WHAT THE CLOUD HOLDS NOW (2026-10-20, `rowFocusRefresh`). The
//   writes above are safe on a stale copy, but a tab left open still SHOWED the rows it loaded —
//   another device's edits, new rows and deletions — until it reloaded or edited that row, and what
//   sums a level, a streak or a list read that copy. When the tab comes back into view (registered
//   with lib/blobFocusRefresh.ts: at most once a minute per store, only once the store has loaded for
//   the account signed in) each store asks for its rows' ids and `updated_at` alone, reads whole only
//   the rows that moved since this device last saw them or are new, and merges them as its load does
//   (`mergeLoad`: ghosts go, unsynced rows stay, a row written here meanwhile keeps this copy, an
//   unsaved edit of a row that moved goes on top of the copy read from its saved base — and that
//   copy becomes its base, so a later load or save merges from it, not from the old one).
//   ⚠️⚠️ READ ONLY: no write-back, no subtask migration, no XP; it records each row's version and
//   base (`seen`, `dirtyBases`), so the next save's compare-and-swap is against what the screen shows.
// - One save in flight per row, reading the row as it is when SENT (so the last write carries
//   the latest state), and a delete waits for the row's save on the wire (so the row's own
//   insert can't land after it).
// ⚠️⚠️ Relies on the table's SELECT policy showing the owner EVERY own row: an UPDATE can only
// see (and return) rows the SELECT policies show, so a hidden row reads as deleted — and is
// dropped here. All six tables' policies are plain `auth.uid() = user_id` (tasks since migration
// 0303; before it, tasks hid old completed rows). A load that filters rows must tell "filtered
// out" from "deleted" itself (`mergeLoad`'s `keep`; tasks read every own id).

/** What changed here while a cloud load was in flight, so its answer can't undo it. */
export interface LoadWindow {
  /** Rows whose insert the cloud confirmed meanwhile: the load's read may have missed them. */
  inserted: Set<string>;
  /** Rows deleted here meanwhile: the load's read may still have them. */
  deleted: Set<string>;
  /**
   * Rows with a write of this device on the wire at any point of the load (sent before it and still
   * out, or sent during it): the load's read may or may not show that write, so its `updated_at`
   * says nothing about another device. This copy stands.
   */
  written: Set<string>;
}

/**
 * ⚠️⚠️ What a load needs to know of each row without a clock (see the header). Persisted with the
 * store, next to `unsyncedIds`, and written by this helper only.
 */
export interface RowMarks<T = unknown> {
  /** Rows edited here (every `save`) whose latest save the cloud hasn't confirmed. */
  dirtyIds: string[];
  /**
   * Each row's `updated_at` (ms) as this device last saw it in the cloud: its load, or its own
   * confirmed write. None: never seen (a cache from before the marks), so nothing says another
   * device has written it since.
   */
  cloudVersions: Partial<Record<string, number>>;
  /**
   * ⚠️⚠️ Each row of `dirtyIds` as the cloud held it at its `cloudVersions` version: the base its
   * unsaved edit is merged from, should another device have written the row since (see the
   * header). Unsaved rows only — a handful. None: a cache from before these (2026-10-20), or a row
   * edited before this device ever saw it (made here) — a load then drops its edit, as before.
   */
  dirtyBases: Partial<Record<string, T>>;
}

/**
 * The marks in a store's state. Only ever consulted for rows the load kept from this account's own
 * copy (`ownCache`), so another account's cache, or one never loaded, adopts nothing through them.
 */
export const marksOf = <T>(s: RowMarks<T>): RowMarks<T> => ({ dirtyIds: s.dirtyIds, cloudVersions: s.cloudVersions, dirtyBases: s.dirtyBases });

/** `dirtyBases` with this row's base set — or, `undefined`, gone. */
function withBase<T>(bases: RowMarks<T>['dirtyBases'], id: string, base: T | undefined): RowMarks<T>['dirtyBases'] {
  const next = { ...bases };
  if (base === undefined) delete next[id];
  else next[id] = base;
  return next;
}

/** An `updated_at` as a version: its time in ms (PostgREST and a client write its format differently). */
const versionOf = (updatedAt: unknown): number | undefined =>
  typeof updatedAt === 'string' ? Date.parse(updatedAt) : undefined;

/** Each row's version, from the rows a load read (raw, with `updated_at`). */
export function cloudVersionsOf(rows: readonly { id: string; updated_at?: unknown }[]): RowMarks['cloudVersions'] {
  return Object.fromEntries(rows.map((r) => [r.id, versionOf(r.updated_at)]));
}

const isoOf = (ms: number) => new Date(ms).toISOString();

/** Writes of one save, before it gives up on a row that keeps moving (the next save tries again). */
const MAX_TRIES = 5;

/** Rows a refresh reads whole per request: their ids travel in the URL. */
const FETCH_IDS = 100;

/**
 * The cloud's copy with this device's edits since `base` on top, field by field (syncDoc's rule:
 * a field unchanged here takes the cloud's; one changed here, this device's — also when the cloud
 * changed it too). Fields are the row's own keys; one missing on a side is `undefined` there.
 */
export function mergeRow<T extends { id: string }>(base: T, mine: T, theirs: T): T {
  const b = base as Record<string, unknown>;
  const m = mine as Record<string, unknown>;
  const t = theirs as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(b), ...Object.keys(m), ...Object.keys(t)])) out[key] = merge3('value', b[key], m[key], t[key]);
  return out as T;
}

export interface OwnRowSyncConfig<T extends { id: string }> {
  table: string;
  /** Log prefix, e.g. 'tasks'. */
  label: string;
  /** The row's columns, without its id (an UPDATE never sets the id). */
  fields: (row: T, userId: string) => Record<string, unknown>;
  /** The row as it is now, read when its write is SENT. Undefined = gone here, nothing to write. */
  current: (id: string) => T | undefined;
  /** The store's row from a cloud row (`select('*')`), as its load reads it. */
  fromRow: (row: Record<string, unknown>) => T;
  /** Show this copy of the row instead of the store's (one the store has: `current` finds it). */
  replace: (row: T) => void;
  /** The store's `unsyncedIds`. */
  unsyncedIds: () => readonly string[];
  /** The cloud confirmed the row's insert: take it off `unsyncedIds`. */
  confirm: (id: string) => void;
  /** The cloud has no row any more: remove it (and its `unsyncedIds` entry) from the store. */
  drop: (id: string) => void;
  /** The store's `RowMarks`. */
  marks: () => RowMarks<T>;
  /** Set them in the store. */
  setMarks: (marks: Partial<RowMarks<T>>) => void;
  /** How the warnings name a row. */
  name: (row: T) => string;
}

export interface OwnRowSync<T extends { id: string }> {
  /** Save these rows, each through its own queue. True when every write was accepted. */
  save: (rows: T | T[]) => Promise<boolean>;
  /** Resolves once any save of these rows already under way has finished (await before a delete). */
  settle: (ids: string | string[]) => Promise<void>;
  /** A row deleted here: a load in flight mustn't bring it back. */
  noteDeleted: (id: string) => void;
  /** Run a load's cloud read, recording what changes here while it is in flight. */
  load: <R>(read: () => PromiseLike<R>) => Promise<{ result: R; win: LoadWindow }>;
  /**
   * ⚠️⚠️ A load has just kept these rows over the cloud's (`mergeLoad`'s `kept`): save them, each
   * through its own queue — an unsynced row is INSERTED, an unsaved edit UPDATED (and dropped here
   * if the cloud no longer has it). Without this they waited on this device for their next edit.
   */
  writeBack: (ids: readonly string[]) => Promise<boolean>;
  /**
   * ⚠️⚠️ These rows are as the cloud holds them at the versions recorded for them: the BASE the
   * next edit of each is merged from, should another device have written it since (see the header).
   * A load's read (`win`: but the rows this device wrote while it was out — the read may predate
   * that write).
   */
  seen: (rows: readonly T[], win?: LoadWindow) => void;
  /**
   * ⚠️⚠️ The store's saved copy, read back (a page load, a change of account): `seen` — but an
   * unsaved row's base is its saved one (`marks.dirtyBases`), not the edited copy shown. Takes the
   * marks read back with the rows: it runs while the store is being created (`onRehydrateStorage`).
   */
  restore: (rows: readonly T[], marks: RowMarks<T>) => void;
  /**
   * ⭐ A focus refresh's read (see the header): run as a load (`LoadWindow`), the ids and versions of
   * the rows a load reads, then only the new rows and those that moved since this device last saw
   * them (`cloudVersions`), whole. Null when nothing moved, nothing is new and nothing held is gone.
   * Throws when the cloud can't be read. READ ONLY.
   */
  readChanges: (args: ReadChanges<T>) => Promise<RowChanges<T> | null>;
}

export interface ReadChanges<T extends { id: string }> {
  /** The rows the store shows (loaded for the account signed in). */
  held: () => readonly T[];
  /** `select('id, updated_at')` with the load's own filter: the rows a load reads. */
  versions: () => PromiseLike<{ data: unknown; error: unknown }>;
  /** Every id the cloud has, for a load whose filter leaves rows out (tasks): what tells them from ghosts. */
  inCloud?: () => PromiseLike<{ ids: Set<string>; error: unknown }>;
  /** A held row the cloud doesn't list that is no ghost all the same (the load's `keep`). */
  keep?: (row: T) => boolean;
}

/** What a focus refresh read (`OwnRowSync.readChanges`). */
export interface RowChanges<T extends { id: string }> {
  win: LoadWindow;
  /** The rows read whole: new, or moved since this device last saw them. */
  fetched: T[];
  /** The cloud has a row with this id (listed, or among `inCloud`'s ids — and not gone before it was read whole). */
  has: (id: string) => boolean;
  /**
   * The cloud's side of `mergeLoad` for the rows the store holds: the rows read whole, and this
   * device's own copy of every other listed row (the cloud holds it at the version this device last
   * saw); their versions; and the window. An unsaved edit of a row that moved goes on top of the
   * copy read, from its saved base (`marks.dirtyBases`), as at a load — and that copy is its base now.
   */
  over: (cached: readonly T[]) => Pick<MergeLoadArgs<T>, 'cloud' | 'versions' | 'win'>;
}

export function createOwnRowSync<T extends { id: string }>(cfg: OwnRowSyncConfig<T>): OwnRowSync<T> {
  /** The loads in flight (a load, a focus refresh — they may overlap): each notes what changes here meanwhile. */
  const windows = new Set<LoadWindow>();
  const note = (what: keyof LoadWindow, id: string) => windows.forEach((w) => w[what].add(id));
  const isUnsynced = (id: string) => cfg.unsyncedIds().includes(id);
  /** How many times each row was saved here (this session): a write confirms the edit it sent only. */
  const edits = new Map<string, number>();

  function markSynced(id: string): void {
    note('inserted', id);
    cfg.confirm(id);
  }

  /** Each row as the cloud holds it at its recorded version, as this device last saw it (see `seen`). */
  const bases = new Map<string, T>();

  /**
   * Rows edited here: unsaved until a write that carries their latest edit is confirmed. A row
   * saved until now keeps the copy its edit was made on (`dirtyBases`); one already unsaved keeps
   * the one it has — the copy shown is no base.
   */
  function markDirty(ids: string[]): void {
    ids.forEach((id) => edits.set(id, (edits.get(id) ?? 0) + 1));
    const { dirtyIds, dirtyBases } = cfg.marks();
    const fresh = ids.filter((id) => !dirtyIds.includes(id));
    cfg.setMarks({
      dirtyIds: [...dirtyIds, ...fresh],
      dirtyBases: fresh.reduce((all, id) => withBase(all, id, bases.get(id)), dirtyBases),
    });
  }

  /**
   * A write of the row landed (or found the cloud holding the edit it carried already): the cloud
   * now holds `copy` at `version` (undefined: not known), and the edit is saved — unless the row
   * was edited again after it was sent (`sentAt`).
   */
  function landed(id: string, sentAt: number, version: number | undefined, copy: T): void {
    const { dirtyIds, cloudVersions, dirtyBases } = cfg.marks();
    const saved = (edits.get(id) ?? 0) === sentAt;
    bases.set(id, copy);
    cfg.setMarks({
      cloudVersions: { ...cloudVersions, [id]: version },
      // Saved: no base to keep. Still unsaved (edited since it was sent): this copy is its base now.
      dirtyBases: withBase(dirtyBases, id, saved ? undefined : copy),
      ...(saved ? { dirtyIds: dirtyIds.filter((x) => x !== id) } : {}),
    });
  }

  /** The cloud took `copy` in place of the `sent` one: show it, with any edit made here since on top. */
  function show(sent: T, copy: T): void {
    const now = cfg.current(sent.id);
    if (now) cfg.replace(mergeRow(sent, now, copy));
  }

  /** The row is gone (deleted here or elsewhere): nothing to remember of it. */
  function forget(id: string): void {
    const { dirtyIds, cloudVersions, dirtyBases } = cfg.marks();
    cfg.setMarks({
      dirtyIds: dirtyIds.filter((x) => x !== id),
      cloudVersions: { ...cloudVersions, [id]: undefined },
      dirtyBases: withBase(dirtyBases, id, undefined),
    });
  }

  function dropDeletedElsewhere(id: string): void {
    const gone = cfg.current(id);
    note('deleted', id);
    cfg.drop(id);
    forget(id);
    console.warn(`[${cfg.label}] "${gone ? cfg.name(gone) : id}" no longer exists in the cloud (deleted on another device or tab) — removed here, not re-created.`);
  }

  /** One whole-row write. True when the database accepted it. */
  async function writeRow(row: T): Promise<boolean> {
    const sentAt = edits.get(row.id) ?? 0;
    note('written', row.id);
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return false;
      if (isUnsynced(row.id)) {
        const { data, error } = await supabase.from(cfg.table).insert([{ id: row.id, ...cfg.fields(row, user.id) }]).select('updated_at');
        if (!error) {
          markSynced(row.id);
          landed(row.id, sentAt, versionOf((data as { updated_at?: unknown }[] | null)?.[0]?.updated_at), row);
          return true;
        }
        // 23505: the row is already there — an earlier insert landed but its answer was lost.
        // It is a cloud row now, so it is UPDATED below like any other.
        if (error.code !== '23505') { console.error(`[${cfg.label}] Failed to save a new row to Supabase:`, error); return false; }
        markSynced(row.id);
      }
      return await updateRow(row, sentAt, user.id);
    } catch (error) {
      console.error(`[${cfg.label}] Error saving to Supabase:`, error);
      return false;
    }
  }

  /**
   * ⚠️⚠️ The UPDATE, as a compare-and-swap on the version this device last saw (see the header):
   * 0 rows reads the row; gone → deleted elsewhere; moved → this device's edits since its base,
   * merged onto the cloud's copy, written the same way. (No version recorded: a plain UPDATE.)
   */
  async function updateRow(row: T, sentAt: number, userId: string): Promise<boolean> {
    const { id } = row;
    const base = bases.get(id);
    let version = cfg.marks().cloudVersions[id];
    let next = row;
    for (let tries = 1; ; tries += 1) {
      let update = supabase.from(cfg.table).update(cfg.fields(next, userId)).eq('id', id);
      if (version !== undefined) update = update.gte('updated_at', isoOf(version)).lt('updated_at', isoOf(version + 1));
      const { data, error } = await update.select('id, updated_at');
      if (error) { console.error(`[${cfg.label}] Failed to save to Supabase:`, error); return false; }
      if (data && data.length > 0) {
        landed(id, sentAt, versionOf((data[0] as { updated_at?: unknown }).updated_at), next);
        if (next !== row) show(row, next);
        return true;
      }
      if (tries === MAX_TRIES) {
        console.warn(`[${cfg.label}] "${cfg.name(row)}" kept changing in the cloud — not saved; the next save tries again.`);
        return false;
      }
      const { data: found, error: readError } = await supabase.from(cfg.table).select('*').eq('id', id);
      if (readError) { console.error(`[${cfg.label}] Failed to read a row back from Supabase:`, readError); return false; }
      const cloudRow = (found as Record<string, unknown>[] | null)?.[0];
      if (!cloudRow) { dropDeletedElsewhere(id); return false; }
      const theirs = cfg.fromRow(cloudRow);
      version = versionOf(cloudRow.updated_at);
      if (!base) {
        // Never seen (the store got the row some other way): nothing here can be told from
        // staleness — the cloud's copy wins, as the load's rule for an unsaved edit of a row that moved.
        console.warn(`[${cfg.label}] "${cfg.name(row)}" was saved on another device since this copy was read — its copy is kept, this one's edit dropped.`);
        landed(id, sentAt, version, theirs);
        show(row, theirs);
        return false;
      }
      next = mergeRow(base, row, theirs);
      // The cloud already holds what this write would send (its own earlier write whose answer was lost).
      if (sameJson(next, theirs)) {
        landed(id, sentAt, version, theirs);
        show(row, theirs);
        return true;
      }
    }
  }

  // ⚠️⚠️ ONE SAVE IN FLIGHT PER ROW (habitStore, found live 2026-10-09): a row is written
  // WHOLE, so two concurrent writes can land out of order and the older state wins. Calls made
  // while one is in flight share ONE follow-up, which reads the row when it is SENT.
  const running = new Map<string, Promise<boolean>>();
  const queued = new Map<string, Promise<boolean>>();
  function saveOne(id: string): Promise<boolean> {
    const next = queued.get(id);
    if (next) return next; // the queued follow-up will read the latest state
    const prev = running.get(id);
    const run: Promise<boolean> = (prev ?? Promise.resolve(true)).then(() => {
      if (queued.get(id) === run) queued.delete(id);
      const latest = cfg.current(id);
      return latest ? writeRow(latest) : true; // deleted meanwhile: nothing to write
    });
    if (prev) queued.set(id, run);
    running.set(id, run);
    void run.finally(() => { if (running.get(id) === run) running.delete(id); });
    return run;
  }

  async function load<R>(read: () => PromiseLike<R>): Promise<{ result: R; win: LoadWindow }> {
    const win: LoadWindow = { inserted: new Set(), deleted: new Set(), written: new Set(running.keys()) };
    windows.add(win);
    try {
      return { result: await read(), win };
    } finally {
      windows.delete(win);
    }
  }

  async function readChanges({ held, versions, inCloud, keep }: ReadChanges<T>): Promise<RowChanges<T> | null> {
    const { result: { listed, ids, read }, win } = await load(async () => {
      const [listing, all] = await Promise.all([versions(), inCloud?.()]);
      if (listing.error) throw listing.error;
      if (all?.error) throw all.error;
      const listed = (listing.data ?? []) as { id: string; updated_at?: unknown }[];
      const seenAt = cfg.marks().cloudVersions;
      const here = new Set(held().map((r) => r.id));
      // New here, or moved since this device last saw it: read whole. Nothing else is downloaded.
      const moved = listed.filter((r) => !here.has(r.id) || seenAt[r.id] !== versionOf(r.updated_at)).map((r) => r.id);
      const read: { id: string; updated_at?: unknown }[] = [];
      for (let i = 0; i < moved.length; i += FETCH_IDS) {
        const { data, error } = await supabase.from(cfg.table).select('*').in('id', moved.slice(i, i + FETCH_IDS));
        if (error) throw error;
        read.push(...((data ?? []) as { id: string }[]));
      }
      return { listed: { rows: listed, moved: new Set(moved) }, ids: all?.ids, read };
    });
    const listedIds = new Set(listed.rows.map((r) => r.id));
    const readIds = new Set(read.map((r) => r.id));
    // Listed as moved, then not there to read whole: deleted between the two reads.
    const has = (id: string) => (ids ?? listedIds).has(id) && !(listed.moved.has(id) && !readIds.has(id));
    if (listed.moved.size === 0 && held().every((r) => has(r.id) || isUnsynced(r.id) || keep?.(r))) return null;
    const fetched = read.map((r) => cfg.fromRow(r));
    const readAt = { ...cloudVersionsOf(listed.rows), ...cloudVersionsOf(read) };
    return {
      win,
      fetched,
      has,
      over: (cached) => ({
        cloud: [...fetched, ...cached.filter((r) => listedIds.has(r.id) && !listed.moved.has(r.id))],
        versions: readAt,
        win,
      }),
    };
  }

  return {
    save: (rows) => {
      const list = (Array.isArray(rows) ? rows : [rows]).filter(Boolean);
      markDirty(list.map((r) => r.id));
      return Promise.all(list.map((r) => saveOne(r.id))).then((ok) => ok.every(Boolean));
    },
    settle: async (ids) => {
      const list = Array.isArray(ids) ? ids : [ids];
      await Promise.all(list.map((id) => running.get(id)));
    },
    noteDeleted: (id) => { note('deleted', id); forget(id); },
    load,
    writeBack: (ids) => Promise.all(ids.map(saveOne)).then((ok) => ok.every(Boolean)),
    seen: (rows, win) => rows.forEach((r) => { if (!win?.written.has(r.id)) bases.set(r.id, r); }),
    restore: (rows, marks) => rows.forEach((r) => bases.set(r.id, marks.dirtyBases[r.id] ?? r)),
    readChanges,
  };
}

/**
 * What of a persisted cache a load may keep, by whose it is. This account's: its rows, with the
 * unsynced ids it recorded (a cache from before `unsyncedIds` has none: all cloud copies). No
 * owner: it has never loaded from the cloud — every load stamps `cachedUserId`, and these stores
 * got that stamp the day they got cloud sync (2026-05-30) — so every row in it was made here.
 * Another account's: nothing.
 */
export function ownCache<T extends { id: string }>(
  cachedUserId: string | null,
  userId: string,
  rows: readonly T[],
  unsyncedIds: readonly string[],
): { cached: readonly T[]; unsyncedIds: readonly string[] } {
  if (cachedUserId === userId) return { cached: rows, unsyncedIds };
  if (cachedUserId === null) return { cached: rows, unsyncedIds: rows.map((r) => r.id) };
  return { cached: [], unsyncedIds: [] };
}

/**
 * ⚠️⚠️ The load merge. A cached row the cloud didn't return was deleted elsewhere — a ghost —
 * and goes, unless it was created here and not yet confirmed (or confirmed while the load was
 * in flight), or `keep` says the cloud still has it (a load that filters rows). The cloud's rows
 * win, except a row deleted here while the load was in flight, a row with a write of this device
 * on the wire during the load (`win.written`), and an unsaved edit (`marks.dirtyIds`) of the row
 * as the cloud still has it: its `updated_at` (`versions`) is the one this device last saw
 * (`marks.cloudVersions`; none recorded: nothing says another device wrote it). An unsaved edit
 * of a row another device has written since goes on top of the cloud's copy, from its saved base
 * (`marks.dirtyBases`) — that copy is its base now; with no saved base, the cloud's copy wins
 * and the edit is dropped (see the header).
 *
 * ⚠️⚠️ `kept` = the rows this device's copy kept over the cloud's that the cloud doesn't have:
 * still unsynced (no row for them), or an unsaved edit. The load saves them
 * (`OwnRowSync.writeBack`) — the way syncBlob's `writeBack` does for blob stores. A row kept by
 * `keep` (left out by the load's filter) or confirmed during the load isn't in it: it was never
 * compared with the cloud's, or the cloud already has it. `marks` = the marks to store.
 *
 * A focus refresh merges with this too (`rowFocusRefresh`) and writes nothing: it never saves `kept`.
 * An unsaved edit it merges stays unsaved — its saved base moved to the copy read — for the next save.
 */
export interface MergeLoadArgs<T extends { id: string }> {
  cached: readonly T[];
  unsyncedIds: readonly string[];
  marks: RowMarks<T>;
  cloud: readonly T[];
  /** The cloud rows' versions (`cloudVersionsOf` the rows read). */
  versions: RowMarks['cloudVersions'];
  win: LoadWindow;
  keep?: (cached: T) => boolean;
  /** How the warnings name the store. */
  label: string;
}

export function mergeLoad<T extends { id: string }>(args: MergeLoadArgs<T>): { rows: T[]; unsyncedIds: string[]; kept: string[]; marks: RowMarks<T> } {
  const cloudIds = new Set(args.cloud.map((r) => r.id));
  const unsynced = new Set(args.unsyncedIds);
  const dirty = new Set(args.marks.dirtyIds);
  const seen = args.marks.cloudVersions;
  const byId = new Map<string, T>();
  const edited: string[] = [];
  const lost: string[] = [];
  /** The cloud's copy each merged edit went on top of: its base from now on. */
  const mergedOnto = new Map<string, T>();
  args.cached.forEach((r) => {
    if (cloudIds.has(r.id) || unsynced.has(r.id) || args.win.inserted.has(r.id) || args.keep?.(r)) byId.set(r.id, r);
  });
  args.cloud.forEach((r) => {
    if (args.win.deleted.has(r.id)) return;
    const local = byId.get(r.id);
    if (local && args.win.written.has(r.id)) {
      if (dirty.has(r.id)) edited.push(r.id);
      return;
    }
    if (local && dirty.has(r.id)) {
      if (seen[r.id] === undefined || seen[r.id] === args.versions[r.id]) { edited.push(r.id); return; }
      const base = args.marks.dirtyBases[r.id];
      if (base) {
        byId.set(r.id, mergeRow(base, local, r));
        mergedOnto.set(r.id, r);
        edited.push(r.id);
        return;
      }
      lost.push(r.id);
    }
    byId.set(r.id, r);
  });
  if (lost.length) {
    console.warn(`[${args.label}] ${lost.length} unsaved edit(s) dropped: another device saved the same row since this copy was loaded — its version is kept.`, lost);
  }
  // Still unconfirmed: kept, and not returned by the cloud.
  const unsyncedIds = [...unsynced].filter((id) => byId.has(id) && !cloudIds.has(id));
  // The version each kept row was last seen at: the read's — but for a row the read didn't show, or
  // one written here meanwhile (whose landed write already recorded what the cloud holds).
  const cloudVersions: RowMarks['cloudVersions'] = {};
  byId.forEach((_, id) => {
    cloudVersions[id] = cloudIds.has(id) && !args.win.written.has(id) ? args.versions[id] : seen[id];
  });
  const dirtyIds = args.marks.dirtyIds.filter((id) => byId.has(id) && !lost.includes(id));
  const dirtyBases = dirtyIds.reduce((all, id) => withBase(all, id, mergedOnto.get(id) ?? args.marks.dirtyBases[id]), {} as RowMarks<T>['dirtyBases']);
  return {
    rows: Array.from(byId.values()),
    unsyncedIds,
    kept: [...unsyncedIds, ...edited],
    marks: { dirtyIds, cloudVersions, dirtyBases },
  };
}

export interface RowFocusRefresh<T extends { id: string }> extends Omit<ReadChanges<T>, 'versions'> {
  /** How the errors name the store. */
  label: string;
  /** The account the store has loaded for since the last change of account (null: not loaded). */
  loadedFor: () => string | null;
  /** `select('id, updated_at')` with the load's own filter, for the account signed in. */
  versions: (userId: string) => PromiseLike<{ data: unknown; error: unknown }>;
  /** Show what was read: the load's merge (`mergeLoad` with `changes.over`), without anything it writes. */
  apply: (changes: RowChanges<T>) => void;
}

/**
 * ⭐ A row store's catch-up when its tab comes back into view (see the header) — for
 * `refreshBlobOnFocus`, which runs it at most once a minute. 'skipped' (not loaded for the account
 * signed in, or another account signed in while the read was out) doesn't spend that turn.
 * ⚠️⚠️ READ ONLY: nothing is written, whatever was read.
 */
export function rowFocusRefresh<T extends { id: string }>(sync: OwnRowSync<T>, o: RowFocusRefresh<T>): () => Promise<RefreshOutcome> {
  return async () => {
    const at = accountGeneration();
    const { user } = await syncManager.safeAuthCheck();
    if (!user || o.loadedFor() !== user.id) return 'skipped';
    let changes: RowChanges<T> | null;
    try {
      changes = await sync.readChanges({ held: o.held, versions: () => o.versions(user.id), inCloud: o.inCloud, keep: o.keep });
    } catch (error) {
      console.error(`[${o.label}] Failed to refresh from Supabase:`, error);
      return 'failed';
    }
    // An answer for an account that has since signed out is not this copy's.
    if (at !== accountGeneration()) return 'skipped';
    if (!changes) return 'unchanged';
    o.apply(changes);
    sync.seen(changes.fetched, changes.win); // the base of each row's next edit
    return 'refreshed';
  };
}
